import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";


import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Heart,
  Plus,
  Loader2,
  UserCircle,
  CalendarIcon,
  Printer,
  Search,
  X,
  ArrowUpDown,
  Church,
  BarChart3
} from "lucide-react";
import { apiClient } from "../lib/apiClient";
import { format } from "date-fns";
import { MarkAnsweredDialog } from "../components/MarkAnsweredDialog";
import { EditPrayerDialog } from "../components/EditPrayerDialog";
import PrintPreview from "../components/PrintPreview";
import { PrayerCard } from "../components/PrayerCard";
import { AnalyticsSection } from "../components/AnalyticsSection";
import { GroupMembersTab } from "../components/GroupMembersTab";
import { TagSelector, PREDEFINED_TAGS } from "../components/TagSelector";
import { DateRangeFilter } from "../components/DateRangeFilter";
import { BulkActionsBar } from "../components/BulkActionsBar";
import { GroupNav } from "../components/GroupNav";
import { GroupHeader } from "../components/GroupHeader";


const EMPTY_GROUPS = [];

const formatDateValue = (date) => {
  if (!date) return null;
  return typeof date === 'string' ? date : date.toISOString();
};

export default function GroupPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [prayers, setPrayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("prayers");
  const [analytics, setAnalytics] = useState(null);
  
  // Dialog states
  const [newPrayerOpen, setNewPrayerOpen] = useState(false);
  const [markAnsweredOpen, setMarkAnsweredOpen] = useState(false);
  const [editPrayerOpen, setEditPrayerOpen] = useState(false);
  const [selectedPrayer, setSelectedPrayer] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortMode, setSortMode] = useState("newest");
  const [filterTag, setFilterTag] = useState("all");
  const [dateStart, setDateStart] = useState(null);
  const [dateEnd, setDateEnd] = useState(null);

  // Bulk selection state
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedPrayerIds, setSelectedPrayerIds] = useState([]);
  
  // Form states
  const [prayerForm, setPrayerForm] = useState({
    request: "",
    notes: "",
    requested_by: "",
    date_requested: null,
    result: "",
    date_answered: null,
    is_hidden: false,
    tags: []
  });
  const [answeredForm, setAnsweredForm] = useState({
    result: "",
    date_answered: null
  });
  const [formLoading, setFormLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameForm, setRenameForm] = useState({ name: "", description: "", church_id: "" });
  const [renameLoading, setRenameLoading] = useState(false);
  const [churches, setChurches] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [groupRes, membersRes, prayersRes, churchesRes, analyticsRes] = await Promise.all([
        apiClient.get("group", groupId),
        apiClient.get("groupMembers", groupId),
        apiClient.get("groupPrayers", groupId),
        apiClient.get("churches"),
        apiClient.get("groupAnalytics", groupId)
      ]);
      setGroup(groupRes.data);
      setMembers(membersRes.data);
      setPrayers(prayersRes.data);
      setChurches(churchesRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/login");
      } else if (err.response?.status === 403) {
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, [groupId, navigate]);  

  useEffect(() => {
    const userData = localStorage.getItem("user");
    
    if (!userData) {
      navigate("/login");
      return;
    }
    
    setUser(JSON.parse(userData));
    fetchData();
  }, [navigate, fetchData]);

  const resetPrayerForm = () => {
    setPrayerForm({
      request: "",
      notes: "",
      requested_by: "",
      date_requested: null,
      result: "",
      date_answered: null,
      is_hidden: false
    });
  };

  const openEditPrayer = (prayer) => {
    setSelectedPrayer(prayer);
    setPrayerForm({
      request: prayer.request,
      notes: prayer.notes || "",
      requested_by: prayer.requested_by || "",
      date_requested: prayer.date_requested ? new Date(prayer.date_requested) : null,
      result: prayer.result || "",
      date_answered: prayer.date_answered ? new Date(prayer.date_answered) : null,
      is_hidden: prayer.is_hidden || false,
      tags: prayer.tags || []
    });
    setEditPrayerOpen(true);
  };

  const updatePrayer = async (e) => {
    e.preventDefault();
    if (!selectedPrayer) return;
    setFormLoading(true);
    try {
      const payload = {
        request: prayerForm.request,
        notes: prayerForm.notes || null,
        requested_by: prayerForm.requested_by || null,
        date_requested: formatDateValue(prayerForm.date_requested),
        result: prayerForm.result || null,
        date_answered: formatDateValue(prayerForm.date_answered),
        is_hidden: prayerForm.is_hidden,
        tags: prayerForm.tags || []
      };
      await apiClient.patch("prayer", selectedPrayer.id, payload);
      setEditPrayerOpen(false);
      setSelectedPrayer(null);
      resetPrayerForm();
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update prayer");
    } finally {
      setFormLoading(false);
    }
  };

  const createPrayer = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        request: prayerForm.request,
        notes: prayerForm.notes || null,
        requested_by: prayerForm.requested_by || null,
        date_requested: prayerForm.date_requested ? prayerForm.date_requested.toISOString() : null,
        group_id: groupId,
        tags: prayerForm.tags.length > 0 ? prayerForm.tags : null
      };
      await apiClient.post("prayers", payload);
      resetPrayerForm();
      setNewPrayerOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create prayer");
    } finally {
      setFormLoading(false);
    }
  };

  const markAsAnswered = async (prayer) => {
    setSelectedPrayer(prayer);
    setAnsweredForm({ result: "", date_answered: new Date() });
    setMarkAnsweredOpen(true);
  };

  const submitMarkAsAnswered = async (e) => {
    e.preventDefault();
    if (!selectedPrayer) return;
    setFormLoading(true);
    try {
      await apiClient.patch("prayer", selectedPrayer.id, { 
          is_answered: true, 
          result: answeredForm.result || "Answered",
          date_answered: answeredForm.date_answered ? answeredForm.date_answered.toISOString() : new Date().toISOString()
        });
      setMarkAnsweredOpen(false);
      setSelectedPrayer(null);
      setAnsweredForm({ result: "", date_answered: null });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to mark as answered");
    } finally {
      setFormLoading(false);
    }
  };

  const openPrintPreview = () => {
    setPrintPreviewOpen(true);
  };

  const openRenameDialog = () => {
    setRenameForm({ name: group.name, description: group.description || "", church_id: group.church_id || "" });
    setRenameOpen(true);
  };

  const submitRename = async (e) => {
    e.preventDefault();
    if (!renameForm.name.trim()) return;
    setRenameLoading(true);
    try {
      const payload = { name: renameForm.name.trim() };
      if (renameForm.description !== (group.description || "")) {
        payload.description = renameForm.description.trim() || null;
      }
      const newChurchId = renameForm.church_id || null;
      if (newChurchId !== (group.church_id || null)) {
        payload.church_id = newChurchId;
      }
      await apiClient.patch("group", groupId, payload);
      setRenameOpen(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update group");
    } finally {
      setRenameLoading(false);
    }
  };

  const deletePrayer = async (prayerId) => {
    try {
      await apiClient.delete("prayer", prayerId);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete prayer");
    }
  };

  const handlePrayedFor = async (prayer) => {
    setPrayers((prev) =>
      prev.map((p) => (p.id === prayer.id ? { ...p, prayer_count: (p.prayer_count || 0) + 1 } : p)),
    );
    try {
      await apiClient.post("prayerPrayedFor", prayer.id);
    } catch (err) {
      setPrayers((prev) =>
        prev.map((p) => (p.id === prayer.id ? { ...p, prayer_count: prayer.prayer_count || 0 } : p)),
      );
      alert(err.response?.data?.detail || "Failed to record prayer");
    }
  };

  const togglePrayerSelect = (prayerId) => {
    setSelectedPrayerIds((prev) =>
      prev.includes(prayerId) ? prev.filter((id) => id !== prayerId) : [...prev, prayerId]
    );
  };

  const cancelBulkSelect = () => {
    setSelectedPrayerIds([]);
    setBulkSelectMode(false);
  };

  const onBulkCompleted = () => {
    setSelectedPrayerIds([]);
    setBulkSelectMode(false);
    fetchData();
  };

  const copyInviteCode = () => {
    if (group) {
      navigator.clipboard.writeText(group.invite_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  // Filter prayers based on search, tag, and date range
  const filterPrayers = (prayerList) => {
    return prayerList.filter((prayer) => {
      const matchesSearch = !searchQuery ||
        prayer.request.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (prayer.requested_by && prayer.requested_by.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (prayer.notes && prayer.notes.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesTag = filterTag === "all" ||
        (prayer.tags && prayer.tags.includes(filterTag));
      
      const prayerDate = new Date(prayer.date_requested || prayer.created_at);
      const matchesDateStart = !dateStart || prayerDate >= dateStart;
      const matchesDateEnd = !dateEnd || prayerDate <= new Date(dateEnd.getTime() + 86400000);
      
      return matchesSearch && matchesTag && matchesDateStart && matchesDateEnd;
    });
  };

  // Sort prayers based on sortMode
  const sortPrayers = (prayerList) => {
    const sorted = [...prayerList];
    if (sortMode === "oldest") {
      sorted.sort((a, b) => new Date(a.date_requested || a.created_at) - new Date(b.date_requested || b.created_at));
    } else if (sortMode === "newest") {
      sorted.sort((a, b) => new Date(b.date_requested || b.created_at) - new Date(a.date_requested || a.created_at));
    } else if (sortMode === "requester") {
      sorted.sort((a, b) => (a.requested_by || "").localeCompare(b.requested_by || ""));
    }
    return sorted;
  };

  // Group prayers by requester
  const groupByRequester = (prayerList) => {
    const grouped = {};
    prayerList.forEach((prayer) => {
      const key = prayer.requested_by || "Unspecified";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(prayer);
    });
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  };

  // Check if current user is an owner (has owner role in members list)
  const isOwner = user && (user.is_admin || members.some(m => m.id === user.id && m.role === "owner"));
  const activePrayers = sortPrayers(filterPrayers(prayers.filter((p) => !p.is_answered)));
  const answeredPrayers = sortPrayers(filterPrayers(prayers.filter((p) => p.is_answered)));

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sage" />
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <p className="text-stone-dark/60">Group not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-light noise-bg">
      <GroupNav
        group={group}
        canRename={isOwner}
        onRename={openRenameDialog}
        copiedCode={copiedCode}
        onCopyInviteCode={copyInviteCode}
      />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <GroupHeader group={group} memberCount={members.length} prayerCount={prayers.length} />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3 mb-6 bg-stone">
            <TabsTrigger value="prayers" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="prayers-tab">
              Prayers ({prayers.length})
            </TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="members-tab">
              Members ({members.length})
            </TabsTrigger>
            <TabsTrigger value="insights" className="data-[state=active]:bg-white text-xs sm:text-sm" data-testid="insights-tab">
              Insights
            </TabsTrigger>
          </TabsList>

          {/* Prayers Tab */}
          <TabsContent value="prayers">
            <div className="flex items-center justify-between mb-4 gap-2">
              <h2 className="font-serif text-lg sm:text-xl font-semibold text-stone-dark shrink-0">Group Prayers</h2>
              <div className="flex items-center gap-2">
                {bulkSelectMode ? (
                  <BulkActionsBar
                    selectedIds={selectedPrayerIds}
                    onCompleted={onBulkCompleted}
                    onCancel={cancelBulkSelect}
                    testIdPrefix="bulk-group"
                  />
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-full text-stone-dark/60"
                      onClick={() => setBulkSelectMode(true)}
                      data-testid="bulk-select-group-btn"
                    >
                      Select
                    </Button>
                <Button 
                  variant="outline" 
                  className="rounded-full"
                  onClick={openPrintPreview}
                  data-testid="print-group-btn"
                >
                  <Printer className="w-4 h-4" />
                  <span className="ml-2 hidden sm:inline">Print</span>
                </Button>
                <Dialog open={newPrayerOpen} onOpenChange={(open) => { setNewPrayerOpen(open); if (!open) resetPrayerForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-sage hover:bg-sage-dark text-white rounded-full" data-testid="new-group-prayer-btn">
                      <Plus className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Add Prayer</span>
                    </Button>
                  </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-serif">Add Prayer to {group.name}</DialogTitle>
                    <DialogDescription>
                      This prayer will be shared with all group members
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={createPrayer} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="request">Prayer Request *</Label>
                      <Textarea
                        id="request"
                        placeholder="What would you like the group to pray for?"
                        value={prayerForm.request}
                        onChange={(e) => setPrayerForm({ ...prayerForm, request: e.target.value })}
                        required
                        className="min-h-24"
                        data-testid="group-prayer-request"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="requested_by">Requested By</Label>
                        <Input
                          id="requested_by"
                          placeholder="Who is this prayer for?"
                          value={prayerForm.requested_by}
                          onChange={(e) => setPrayerForm({ ...prayerForm, requested_by: e.target.value })}
                          data-testid="group-prayer-requested-by"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Date Requested</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {prayerForm.date_requested ? format(prayerForm.date_requested, "PPP") : "Select date"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={prayerForm.date_requested}
                              onSelect={(date) => setPrayerForm({ ...prayerForm, date_requested: date })}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        placeholder="Any additional details..."
                        value={prayerForm.notes}
                        onChange={(e) => setPrayerForm({ ...prayerForm, notes: e.target.value })}
                        className="min-h-16"
                        data-testid="group-prayer-notes"
                      />
                    </div>

                    <TagSelector
                      selectedTags={prayerForm.tags}
                      onChange={(tags) => setPrayerForm({ ...prayerForm, tags })}
                      testIdPrefix="group-prayer-"
                    />
                    
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button type="button" variant="outline" className="rounded-full">
                          Cancel
                        </Button>
                      </DialogClose>
                      <Button 
                        type="submit" 
                        className="bg-sage hover:bg-sage-dark text-white rounded-full"
                        disabled={formLoading}
                        data-testid="submit-group-prayer-btn"
                      >
                        {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Prayer"}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
              </>
                )}
              </div>
            </div>

            {/* Search and Sort Bar */}
            {prayers.length > 0 && (
              <>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-dark/40" />
                  <Input
                    placeholder="Search prayers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10 bg-white"
                    data-testid="search-group-prayers"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-dark/40 hover:text-stone-dark"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Select value={sortMode} onValueChange={setSortMode}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="sort-group-prayers">
                    <ArrowUpDown className="w-4 h-4 mr-2 text-stone-dark/50" />
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Date (Newest First)</SelectItem>
                    <SelectItem value="oldest">Date (Oldest First)</SelectItem>
                    <SelectItem value="requester">By Requester</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <Select value={filterTag} onValueChange={setFilterTag}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="filter-group-tag">
                    <SelectValue placeholder="All tags" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All tags</SelectItem>
                    {PREDEFINED_TAGS.map((tag) => (
                      <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <DateRangeFilter
                  startDate={dateStart}
                  endDate={dateEnd}
                  onStartChange={setDateStart}
                  onEndChange={setDateEnd}
                  onClear={() => { setDateStart(null); setDateEnd(null); }}
                />
              </div>
              </>
            )}

            <div className="grid gap-4">
              {prayers.length === 0 ? (
                <Card className="border-stone border-dashed">
                  <CardContent className="p-8 text-center">
                    <Heart className="w-12 h-12 text-stone-dark/20 mx-auto mb-4" />
                    <p className="text-stone-dark/60">No prayers in this group yet</p>
                    <p className="text-sm text-stone-dark/40 mt-1">Click "Add Prayer" to share a prayer request</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {activePrayers.length > 0 && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-stone-dark/60 uppercase tracking-wide">Active Prayers</h3>
                      {sortMode === "requester" ? (
                        groupByRequester(activePrayers).map(([requester, prayerGroup]) => (
                          <div key={requester} className="space-y-3" data-testid={`group-requester-${requester}`}>
                            <div className="flex items-center gap-2 pt-1">
                              <UserCircle className="w-5 h-5 text-sage" />
                              <h4 className="font-serif text-base font-semibold text-sage">{requester}</h4>
                              <span className="text-xs text-stone-dark/40 bg-stone/50 px-2 py-0.5 rounded-full">{prayerGroup.length}</span>
                            </div>
                            {prayerGroup.map((prayer) => (
                              <PrayerCard
                                key={prayer.id}
                                prayer={prayer}
                                canModify={prayer.requestor_id === user?.id || isOwner}
                                showRequestor
                                onMarkAnswered={() => markAsAnswered(prayer)}
                                onEdit={() => openEditPrayer(prayer)}
                                onDelete={() => deletePrayer(prayer.id)}
                                onPrayedFor={() => handlePrayedFor(prayer)}
                                formatDate={formatDate}
                                selectable={bulkSelectMode}
                                selected={selectedPrayerIds.includes(prayer.id)}
                                onSelectToggle={togglePrayerSelect}
                              />
                            ))}
                          </div>
                        ))
                      ) : (
                        activePrayers.map((prayer) => (
                          <PrayerCard
                            key={prayer.id}
                            prayer={prayer}
                            canModify={prayer.requestor_id === user?.id || isOwner}
                            showRequestor
                            onMarkAnswered={() => markAsAnswered(prayer)}
                            onEdit={() => openEditPrayer(prayer)}
                            onDelete={() => deletePrayer(prayer.id)}
                            onPrayedFor={() => handlePrayedFor(prayer)}
                            formatDate={formatDate}
                            selectable={bulkSelectMode}
                            selected={selectedPrayerIds.includes(prayer.id)}
                            onSelectToggle={togglePrayerSelect}
                          />
                        ))
                      )}
                    </div>
                  )}
                  
                  {answeredPrayers.length > 0 && (
                    <div className="space-y-4 mt-8">
                      <h3 className="text-sm font-medium text-stone-dark/60 uppercase tracking-wide">Answered Prayers</h3>
                      {sortMode === "requester" ? (
                        groupByRequester(answeredPrayers).map(([requester, prayerGroup]) => (
                          <div key={requester} className="space-y-3" data-testid={`group-requester-answered-${requester}`}>
                            <div className="flex items-center gap-2 pt-1">
                              <UserCircle className="w-5 h-5 text-gold" />
                              <h4 className="font-serif text-base font-semibold text-gold">{requester}</h4>
                              <span className="text-xs text-stone-dark/40 bg-stone/50 px-2 py-0.5 rounded-full">{prayerGroup.length}</span>
                            </div>
                            {prayerGroup.map((prayer) => (
                              <PrayerCard
                                key={prayer.id}
                                prayer={prayer}
                                canModify={prayer.requestor_id === user?.id || isOwner}
                                showRequestor
                                onEdit={() => openEditPrayer(prayer)}
                                onDelete={() => deletePrayer(prayer.id)}
                                formatDate={formatDate}
                                isAnswered
                              />
                            ))}
                          </div>
                        ))
                      ) : (
                        answeredPrayers.map((prayer) => (
                          <PrayerCard
                            key={prayer.id}
                            prayer={prayer}
                            canModify={prayer.requestor_id === user?.id || isOwner}
                            showRequestor
                            onEdit={() => openEditPrayer(prayer)}
                            onDelete={() => deletePrayer(prayer.id)}
                            formatDate={formatDate}
                            isAnswered
                          />
                        ))
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members">
            <GroupMembersTab
              group={group}
              members={members}
              user={user}
              isOwner={isOwner}
              onDataRefresh={fetchData}
            />
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights" data-testid="insights-content">
            {!analytics || analytics.total === 0 ? (
              <div className="mt-6">
                <Card className="border-stone border-dashed">
                  <CardContent className="p-12 text-center">
                    <BarChart3 className="w-12 h-12 text-stone-dark/15 mx-auto mb-4" />
                    <p className="text-stone-dark/60">No prayer data yet</p>
                    <p className="text-sm text-stone-dark/40 mt-1">Analytics will appear once prayers are added to this group</p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <AnalyticsSection analytics={analytics} title="Group Prayer Insights" testIdPrefix="group-analytics" />
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Mark as Answered Dialog */}
      <MarkAnsweredDialog
        open={markAnsweredOpen}
        onOpenChange={(open) => { setMarkAnsweredOpen(open); if (!open) { setSelectedPrayer(null); setAnsweredForm({ result: "", date_answered: null }); } }}
        selectedPrayer={selectedPrayer}
        answeredForm={answeredForm}
        setAnsweredForm={setAnsweredForm}
        onSubmit={submitMarkAsAnswered}
        formLoading={formLoading}
        testIdPrefix="group-"
      />

      {/* Edit Prayer Dialog */}
      <EditPrayerDialog
        open={editPrayerOpen}
        onOpenChange={(open) => { setEditPrayerOpen(open); if (!open) { setSelectedPrayer(null); resetPrayerForm(); } }}
        prayerForm={prayerForm}
        setPrayerForm={setPrayerForm}
        onSubmit={updatePrayer}
        formLoading={formLoading}
        testIdPrefix="group-"
      />


      {/* Edit Group Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif">Edit Group</DialogTitle>
            <DialogDescription>Update the group name, description, and church association.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitRename} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="groupName">Group Name</Label>
              <Input
                id="groupName"
                value={renameForm.name}
                onChange={(e) => setRenameForm({ ...renameForm, name: e.target.value })}
                placeholder="Enter group name"
                required
                className="bg-stone-light"
                data-testid="rename-group-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="groupDesc">Description (optional)</Label>
              <Input
                id="groupDesc"
                value={renameForm.description}
                onChange={(e) => setRenameForm({ ...renameForm, description: e.target.value })}
                placeholder="Brief description"
                className="bg-stone-light"
                data-testid="rename-group-desc"
              />
            </div>
            <div className="space-y-2">
              <Label>Church (optional)</Label>
              <Select
                value={renameForm.church_id || "none"}
                onValueChange={(value) => setRenameForm({ ...renameForm, church_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="bg-stone-light" data-testid="rename-group-church-select">
                  <SelectValue placeholder="No church association" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No church association</SelectItem>
                  {churches.map((church) => (
                    <SelectItem key={church.id} value={church.id}>
                      <span className="flex items-center gap-2">
                        <Church className="w-3.5 h-3.5 text-sage" />
                        {church.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
              </DialogClose>
              <Button 
                type="submit" 
                className="bg-sage hover:bg-sage-dark text-white rounded-full"
                disabled={renameLoading || !renameForm.name.trim()}
                data-testid="rename-group-submit"
              >
                {renameLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Preview */}
      <PrintPreview
        isOpen={printPreviewOpen}
        onClose={() => setPrintPreviewOpen(false)}
        prayers={sortPrayers(filterPrayers(prayers))}
        title={group?.name || "Group Prayer List"}
        subtitle={group?.description}
        showGroupColumn={false}
        groups={EMPTY_GROUPS}
        sortMode={sortMode}
      />
    </div>
  );
}

// Prayer Card Component
